import {
  AccountApi,
  AiChatApi,
  ColormapsApi,
  type Configuration,
  ContentApi,
  CountryPlotApi,
  CustomGeojsonApi,
  EnergyPlotApi,
  FileApi,
  GeojsonApi,
  LayerApi,
  LayoutApi,
  LocationTableApi,
  MapApi,
  MarketplaceApi,
  NoteApi,
  PlotApi,
  ProfileApi,
  type ResponseContext,
  SearchApi,
  SecurityApi,
  SessionApi,
  SoundingApi,
  TabApi,
  TephigramApi,
  TimeStateApi,
  UserApi,
  ViewportApi,
  WeatherTableApi,
} from "@mm/metx-workbench.meteomatics.com";

import { oAuthConfiguration } from "@/auth/configuration";
import { InvalidSessionNotification } from "@/auth/device-tracking-session";
import { getRoot } from "@/renderRoot";
import { i18n } from "@lingui/core";
import { I18nProvider } from "@lingui/react";
import { type Configuration as EnergyConfig, EnergyForecastApi } from "@mm/energy.metx.meteomatics.com";
import React from "react";

const ERROR_MSG = "API isn't initialized! Did you forgot to call initialize(configuration) on app startup?";
const handleError = (context: ResponseContext) => {
  const { response } = context;

  // Disable session on 460
  if (response.status === 460) {
    getRoot().render(
      <I18nProvider i18n={i18n}>
        <InvalidSessionNotification />,
      </I18nProvider>,
    );
    throw context.response;
  }
  if (response.status === 403) {
    const queryParams = new URLSearchParams({
      client_id: oAuthConfiguration.clientId,
      redirect_url: window.location.origin,
    });
    const signOutUrl = `${oAuthConfiguration.oauthLogoutUrl}?${queryParams.toString()}`;
    window.localStorage.setItem("auth", "null");
    window.location.replace(signOutUrl);
    return Promise.resolve(context.response);
  }
  return Promise.resolve(context.response);
};

export class Api {
  private static instance: Api;

  private configuration: Configuration;

  private accountApi: AccountApi;
  private countryPlotApi: CountryPlotApi;
  private colormapsApi: ColormapsApi;
  private customGeojsonApi: CustomGeojsonApi;
  private energyForecastApi: EnergyForecastApi;
  private energyPlotApi: EnergyPlotApi;
  private fileApi: FileApi;
  private geojsonApi: GeojsonApi;
  private layerApi: LayerApi;
  private layoutApi: LayoutApi;
  private locationTableApi: LocationTableApi;
  private mapApi: MapApi;
  private marketplaceApi: MarketplaceApi;
  private noteApi: NoteApi;
  private plotApi: PlotApi;
  private profileApi: ProfileApi;
  private sessionApi: SessionApi;
  private securityApi: SecurityApi;
  private soundingApi: SoundingApi;
  private tabApi: TabApi;
  private tephigramApi: TephigramApi;
  private timeStateApi: TimeStateApi;
  private userApi: UserApi;
  private viewportApi: ViewportApi;
  private weatherTableApi: WeatherTableApi;
  private contentApi: ContentApi;
  private aiChatApi: AiChatApi;
  private searchApi: SearchApi;

  private constructor(configuration: Configuration, energyConfig: EnergyConfig) {
    this.configuration = configuration;

    this.accountApi = new AccountApi(configuration).withPostMiddleware(handleError);
    this.countryPlotApi = new CountryPlotApi(configuration).withPostMiddleware(handleError);
    this.colormapsApi = new ColormapsApi(configuration).withPostMiddleware(handleError);
    this.customGeojsonApi = new CustomGeojsonApi(configuration).withPostMiddleware(handleError);
    this.energyForecastApi = new EnergyForecastApi(energyConfig).withPostMiddleware(handleError);
    this.energyPlotApi = new EnergyPlotApi(configuration).withPostMiddleware(handleError);
    this.fileApi = new FileApi(configuration).withPostMiddleware(handleError);
    this.geojsonApi = new GeojsonApi(configuration).withPostMiddleware(handleError);
    this.layerApi = new LayerApi(configuration).withPostMiddleware(handleError);
    this.layoutApi = new LayoutApi(configuration).withPostMiddleware(handleError);
    this.locationTableApi = new LocationTableApi(configuration).withPostMiddleware(handleError);
    this.mapApi = new MapApi(configuration).withPostMiddleware(handleError);
    this.marketplaceApi = new MarketplaceApi(configuration).withPostMiddleware(handleError);
    this.noteApi = new NoteApi(configuration).withPostMiddleware(handleError);
    this.plotApi = new PlotApi(configuration).withPostMiddleware(handleError);
    this.profileApi = new ProfileApi(configuration).withPostMiddleware(handleError);
    this.sessionApi = new SessionApi(configuration).withPostMiddleware(handleError);
    this.securityApi = new SecurityApi(configuration).withPostMiddleware(handleError);
    this.soundingApi = new SoundingApi(configuration).withPostMiddleware(handleError);
    this.tabApi = new TabApi(configuration).withPostMiddleware(handleError);
    this.tephigramApi = new TephigramApi(configuration).withPostMiddleware(handleError);
    this.timeStateApi = new TimeStateApi(configuration).withPostMiddleware(handleError);
    this.userApi = new UserApi(configuration).withPostMiddleware(handleError);
    this.viewportApi = new ViewportApi(configuration).withPostMiddleware(handleError);
    this.weatherTableApi = new WeatherTableApi(configuration).withPostMiddleware(handleError);
    this.contentApi = new ContentApi(configuration).withPostMiddleware(handleError);
    this.aiChatApi = new AiChatApi(configuration).withPostMiddleware(handleError);
    this.searchApi = new SearchApi(configuration).withPostMiddleware(handleError);
  }

  public static get account() {
    if (!Api.instance) {
      throw new Error(ERROR_MSG);
    }

    return Api.instance.accountApi;
  }

  public static get timeState() {
    if (!Api.instance) {
      throw new Error(ERROR_MSG);
    }

    return Api.instance.timeStateApi;
  }

  public static get tab() {
    if (!Api.instance) {
      throw new Error(ERROR_MSG);
    }

    return Api.instance.tabApi;
  }

  public static get layer() {
    if (!Api.instance) {
      throw new Error(ERROR_MSG);
    }

    return Api.instance.layerApi;
  }

  public static get profile() {
    if (!Api.instance) {
      throw new Error(ERROR_MSG);
    }

    return Api.instance.profileApi;
  }

  public static get cartographicMap() {
    if (!Api.instance) {
      throw new Error(ERROR_MSG);
    }

    return Api.instance.mapApi;
  }

  public static get marketplace() {
    if (!Api.instance) {
      throw new Error(ERROR_MSG);
    }

    return Api.instance.marketplaceApi;
  }

  public static get plot() {
    if (!Api.instance) {
      throw new Error(ERROR_MSG);
    }

    return Api.instance.plotApi;
  }

  public static get energyPlot() {
    if (!Api.instance) {
      throw new Error(ERROR_MSG);
    }

    return Api.instance.energyPlotApi;
  }

  public static get countryForecastPlot() {
    if (!Api.instance) {
      throw new Error(ERROR_MSG);
    }

    return Api.instance.countryPlotApi;
  }

  public static get weatherTable() {
    if (!Api.instance) {
      throw new Error(ERROR_MSG);
    }

    return Api.instance.weatherTableApi;
  }

  public static get locationTable() {
    if (!Api.instance) {
      throw new Error(ERROR_MSG);
    }

    return Api.instance.locationTableApi;
  }

  public static get viewport() {
    if (!Api.instance) {
      throw new Error(ERROR_MSG);
    }

    return Api.instance.viewportApi;
  }

  public static get layout() {
    if (!Api.instance) {
      throw new Error(ERROR_MSG);
    }

    return Api.instance.layoutApi;
  }

  public static get user() {
    if (!Api.instance) {
      throw new Error(ERROR_MSG);
    }

    return Api.instance.userApi;
  }

  public static get session() {
    if (!Api.instance) {
      throw new Error(ERROR_MSG);
    }

    return Api.instance.sessionApi;
  }

  public static get security() {
    if (!Api.instance) {
      throw new Error(ERROR_MSG);
    }

    return Api.instance.securityApi;
  }

  public static get energyForecast() {
    if (!Api.instance) {
      throw new Error(ERROR_MSG);
    }

    return Api.instance.energyForecastApi;
  }

  public static get tephigram() {
    if (!Api.instance) {
      throw new Error(ERROR_MSG);
    }

    return Api.instance.tephigramApi;
  }

  public static get customGeojson() {
    if (!Api.instance) {
      throw new Error(ERROR_MSG);
    }

    return Api.instance.customGeojsonApi;
  }
  public static get file() {
    if (!Api.instance) {
      throw new Error(ERROR_MSG);
    }
    return Api.instance.fileApi;
  }
  public static get geojson() {
    if (!Api.instance) {
      throw new Error(ERROR_MSG);
    }
    return Api.instance.geojsonApi;
  }

  public static get notes() {
    if (!Api.instance) {
      throw new Error(ERROR_MSG);
    }

    return Api.instance.noteApi;
  }

  public static get sounding() {
    if (!Api.instance) {
      throw new Error(ERROR_MSG);
    }

    return Api.instance.soundingApi;
  }

  public static get content() {
    if (!Api.instance) {
      throw new Error(ERROR_MSG);
    }

    return Api.instance.contentApi;
  }

  public static get aiChat() {
    if (!Api.instance) {
      throw new Error(ERROR_MSG);
    }

    return Api.instance.aiChatApi;
  }

  public static get search() {
    if (!Api.instance) {
      throw new Error(ERROR_MSG);
    }

    return Api.instance.searchApi;
  }

  public static get config() {
    if (!Api.instance) {
      throw new Error(ERROR_MSG);
    }

    return Api.instance.configuration;
  }

  public static get colormaps() {
    if (!Api.instance) {
      throw new Error(ERROR_MSG);
    }

    return Api.instance.colormapsApi;
  }

  /**
   * Generic, token-aware fetch helper for ad-hoc endpoints.
   *
   * Uses the same access token as the configured OpenAPI clients and
   * applies the same error handling semantics.
   *
   * @typeParam T - Expected response type.
   * @param input - Absolute or relative URL to fetch.
   * @param init - Optional fetch init configuration (method, headers, body, etc.).
   * @param parseAs - How to parse the response body. Defaults to 'auto'.
   *   - 'auto': parse JSON when content-type is JSON or when body looks like JSON; otherwise return text
   *   - 'json': always parse as JSON
   *   - 'text': always parse as text
   * @returns Parsed response of type `T`.
   * @throws Error when the response is not OK. Mirrors handleError behavior for 403/460, etc.
   */
  public static async apiGeneric<T = unknown>(
    input: string | URL,
    init?: RequestInit,
    parseAs: "auto" | "json" | "text" = "auto",
  ): Promise<T> {
    if (!Api.instance) {
      throw new Error(ERROR_MSG);
    }

    // Resolve bearer token via the same configuration used by our typed clients
    const token = Api.instance.configuration.accessToken ? await Api.instance.configuration.accessToken() : undefined;

    // Merge headers and inject Authorization
    const headers = new Headers(init?.headers || {});
    if (token && !headers.has("Authorization")) {
      headers.set("Authorization", `Bearer ${token}`);
    }

    const response = await fetch(input, { ...init, headers });

    if (!response.ok) {
      // Reuse existing error handling for consistency (logout, invalid session, etc.)
      await handleError({ response } as unknown as ResponseContext);
      // If handleError didn't throw, throw a generic fetch error
      throw new Error(`apiGeneric request failed: ${response.status} ${response.statusText}`);
    }

    // Parse the body according to parseAs strategy
    if (parseAs === "text") {
      return (await response.text()) as unknown as T;
    }
    if (parseAs === "json") {
      return (await response.json()) as T;
    }

    // auto-detect
    const contentType = response.headers.get("content-type") || "";
    if (contentType.includes("application/json")) {
      return (await response.json()) as T;
    }

    const text = await response.text();
    try {
      return JSON.parse(text) as T;
    } catch {
      return text as unknown as T;
    }
  }

  public static initialize(configuration: Configuration, energyConfig: EnergyConfig) {
    Api.instance = new Api(configuration, energyConfig);
  }
}
